import React, {useState, useEffect, useCallback, useRef, useMemo} from "react"
import {useDispatch, useSelector} from "react-redux"
import _ from "lodash"
import moment from "moment"
import {useAuth0} from "@auth0/auth0-react"

// UI
import {Card} from "@uprise/card"
import {spacing} from "@uprise/spacing"
import {Modal} from "@uprise/modal"
import {H4} from "@uprise/headings"
import {Row, Col} from "@uprise/grid"

// Slices
import {fetchSesssionData, listEmployers, searchEmployers, sessionDataSelector, updateSessions} from "./sessionSlice"
import {authSelector} from "../Auth/authSlice"

// Components
import ComponentWrapper from "components/HOC/ComponentWrapper"
import TableComponent from "components/shared/Table"
import ViewRequest from "./ViewRequest"
import StatusButton from "./StatusButton"
import {Alerts} from "./Alerts"
import AutoComplete from "components/shared/AutoComplete"
import IconSearch from "assets/images/icons/svg/search.svg"

// Styles
import {SearchUserInput, HeaderTableContent, ModalButtonContainer} from "features/AdditionalSession/index.style"
import {SearchInputContainer, SearchInputfigure, SearchInputImage} from "features/RiskCallBacks/index.style"

const AdditionalSession = () => {
	const [tableLoading, setTableLoading] = useState(false)
	const [token, setToken] = useState(null)
	const [pageCount, setPageCount] = useState(0)
	const [openModal, setOpenModal] = useState(false)
	const [rowData, setRowData] = useState([])
	const [tableData, setTableData] = useState([])
	const [employeeResults, setEmployeeResults] = useState([])
	const [search, setSearch] = useState("")
	const [resetPages, setResetPages] = useState(false)
	const [selectedItem, setSelectedItem] = useState("")
	const [loading, setLoading] = useState(false)

	const {getAccessTokenSilently, isLoading, user} = useAuth0()

	const handleClick = row => {
		setRowData(row)
		setOpenModal(true)
	}

	const fetchIdRef = useRef(0)
	const dispatch = useDispatch()

	const {fetchSesssionDataApiState, updateSessionsApiState, searchEmployersApiState, listEmployersApiState} =
		useSelector(sessionDataSelector)
	const authState = useSelector(authSelector)

	useEffect(() => {
		async function getToken() {
			if (!isLoading) {
				const token = await getAccessTokenSilently()
				setToken(token)
				dispatch(listEmployers({token}))
			}
		}

		getToken()
	}, [isLoading, user])

	const resetPagination = async () => {
		setResetPages(true)
		await new Promise(r => setTimeout(r, 2000))
		setResetPages(false)
	}

	useEffect(() => {
		arrangeEmployees()
	}, [listEmployersApiState.data])

	useEffect(() => {
		resetPagination()
	}, [search, selectedItem])

	useEffect(() => {
		setTableLoading(false)
		setTableData(fetchSesssionDataApiState.data)
		setPageCount(Math.ceil(fetchSesssionDataApiState.total / 20))
	}, [fetchSesssionDataApiState, searchEmployersApiState])

	const arrangeEmployees = () => {
		setEmployeeResults(listEmployersApiState.data)
		setLoading(false)
	}

	const fetchData = useCallback(
		({pageSize, pageIndex}) => {
			// This will get called when the table needs new data
			// You could fetch your data from literally anywhere,
			// even a server. But for this example, we'll just fake it.

			// Give this fetch an ID
			const fetchId = ++fetchIdRef.current
			setTableLoading(true)
			if (fetchId === fetchIdRef.current && token && authState.data && authState.data.id) {
				dispatch(
					fetchSesssionData({
						limit: pageSize,
						offset: pageSize * pageIndex,
						search,
						user,
						token
					})
				)
			}
		},
		[token, authState, search]
	)

	const columns = useMemo(
		() => [
			{
				Header: "Requestor's Name",
				accessor: "userName",
				Cell: cellInfo => (
					<div onClick={() => handleClick(cellInfo.row.original)}>{cellInfo.row.original.userName}</div>
				),
				show: true
			},
			{
				Header: "Requestor's Email",
				accessor: "userEmail",
				Cell: cellInfo => <div style={{textAlign: "left"}}>{cellInfo.row.original.userEmail}</div>,
				show: true
			},
			{
				Header: "Company Name",
				accessor: "companyName",
				Cell: cellInfo => <div style={{textAlign: "left"}}>{cellInfo.row.original.companyName}</div>,
				show: true
			},
			{
				Header: "Date Requested",
				accessor: "createdAt",
				desc: true,
				sortType: "string",
				Cell: cellInfo => <div>{moment(cellInfo.row.original.createdAt).format("DD-MMM-YYYY")}</div>,
				show: true
			},
			{
				Header: "Requester",
				accessor: "coachName",
				show: true
			},
			{
				Header: "Number of Sessions",
				accessor: "sessions",
				show: true
			},
			{
				Header: "Status",
				width: "100px",
				accessor: "status",
				Cell: cellInfo => <StatusButton status={cellInfo.row.original.status} />,
				show: true
			}
		],
		[tableData]
	)

	const handleComplete = data => {
		const {remarks, invoice, id} = data
		dispatch(
			updateSessions({
				id,
				remarks,
				invoice,
				token
			})
		)
		setOpenModal(false)
	}

	const handleChange = value => {
		fetchSearch(value)
	}

	const fetchSearch = useCallback(
		_.debounce(
			async value => {
				setLoading(true)
				let newToken = token
				// extract when user selects from dropdown
				const selected = value.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi)
				let searchValue = value
				//prevents fetchData to be called as a side effect
				if (selected) {
					setSearch(selected[0])
					setSelectedItem(selected[0])
					searchValue = selected[0]
				} else {
					setSearch(value)
					setSelectedItem("")
				}
				if (!token) newToken = await getAccessTokenSilently()
				if (value.trim()) {
					dispatch(searchEmployers({search: searchValue, token: newToken}))
				}
			},
			1000,
			{maxWait: 1000}
		),
		[]
	)

	const handleSearch = useCallback(
		_.debounce(
			value => {
				setSearch(value)
			},
			1500,
			{maxWait: 1500}
		),
		[]
	)

	return (
		<ComponentWrapper
			menuActiveIndex={2}
			subMenuActiveIndex={11}
			headerTitle='Additional Sessions'
			headerRightAlignContent={<HeaderTableContent></HeaderTableContent>}>
			<Card padding={spacing.s10} backgroundColor='white'>
				<Row className='m-b-10'>
					<Col className='col-lg-6 col-md-6 col-sm-12'>
						<SearchInputContainer>
							<SearchInputfigure>
								<SearchInputImage src={IconSearch} />
							</SearchInputfigure>
							<SearchUserInput
								type={"text"}
								label={"email"}
								//value={search}
								onChange={e => handleSearch(e.target.value)}
								placeholder={"Search by Name, Email or Company Name"}
								data-testid='searchUserEmail'
								isRequired={false}
							/>{" "}
						</SearchInputContainer>
					</Col>
				</Row>

				<Alerts updateSessionsApiState={updateSessionsApiState} />

				<TableComponent
					loading={
						fetchSesssionDataApiState.isError
							? false
							: fetchSesssionDataApiState.isFetching || !fetchSesssionDataApiState.isSuccess
					}
					pageCount={pageCount}
					fetchData={fetchData}
					columns={columns}
					data={tableData || []}
					autoResetPage={resetPages}
					alignHeader={"start"}
				/>
			</Card>
			<Modal
				isOpen={openModal}
				handleClose={() => setOpenModal(false)}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"70%"}
				scroll={true}
				textAlign='center'>
				<H4>{`View Request`}</H4>
				<ModalButtonContainer>
					<ViewRequest onCancel={() => setOpenModal(false)} data={rowData} onComplete={handleComplete} />
				</ModalButtonContainer>
			</Modal>
		</ComponentWrapper>
	)
}

export default AdditionalSession
